// @ts-check
/** @typedef {import('./types/user.types').UserDetails} UserDetails */

import { useAuthenticatedQuery } from './useAuthenticatedQuery';
import axios from 'axios';
import { API_BASE_URL, FUNCTION_KEY, PROJECT_SCOPE } from '../config';
import { useAuth } from '../components/navigation/Authentication.jsx';
import logError, { toError } from './metadata/ErrorLogging';

/**
 * Custom data hook to fetch user details from the API.
 * Uses authenticated query to ensure the user has permission to call the API.
 *
 * @returns {{
 *   user: UserDetails | null,
 *   isLoading: boolean,
 *   refetch: () => Promise<unknown> | unknown,
 *   error: Error | null
 * }} The user data and related information.
 */
// Custom data hook to fetch user details from the API
export default function useUser() {
  // Get the authenticated user from the useAuth hook
  const { user: authUser } = useAuth();

  /**
   *  Function to fetch user details from the API
   * 
   *  @param {string} token
   *  @returns {Promise<UserDetails>}
   */
  const fetchUserDetails = async (token) => {
    // Make a GET request to the API endpoint with the user token and function key
    const response = await axios.get(`${API_BASE_URL}user_details`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'x-functions-key': FUNCTION_KEY
      },
    });
    // Return the response data
    return response.data;
  };

  // Define the user scope for the authenticated query
  const userScope = [PROJECT_SCOPE];

  const { data, isLoading, refetch, error } =
    useAuthenticatedQuery(`user`, fetchUserDetails, userScope, {
      staleTime: 5 * 60 * 1000,
      enabled: !!authUser?.localAccountId,
      onError: (e) => logError('Failed to load user details in useUser', e, {}),
    });

  /** @type {UserDetails | null} */
  const user = data ?? null;

  const parsedError = error ? toError(error) : null;
  return { user, isLoading, refetch, error: parsedError };
}